import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import React, { useEffect, useMemo, useRef } from 'react';

import { addCalendarDays, type DayKey } from '@cineamo/ui/src/helpers/cinema-time-zone';
import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import { useFormat } from '@cineamo/ui/src/hooks/formatter/useFormat';
import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';

import { useCinemasShowingsSearch } from '../hooks/useCinemasShowingsSearch';
import { useEarliestShowingDate } from '../hooks/useEarliestShowingDate';
import { useAnalytics } from '../providers/analyticsProvider';
import { useRequiredShowtimesFinder } from '../providers/ShowtimesFinderProvider';
import { SHOWTIMES_TIME_ZONE, showtimesDayStart, todayShowtimesDayKey } from '../showtimes/showtimesDay';
import useLocationStore from '../stores/location/useLocationStore';

import {
    getActiveDateTab,
    getDateButtonsStartDayKey,
    isBeforeReleaseDate,
    isSelectedDateOnStrip,
    PREVIEWS_SELECTED_DATE,
    resolveFallbackSelectedDate
} from './previewsUtils';

// One placeholder per button the resolved strip will have: ALL plus the seven days.
const DATE_BUTTON_SKELETON_KEYS = ['all', 'day-0', 'day-1', 'day-2', 'day-3', 'day-4', 'day-5', 'day-6'];

const DateButton: React.FC<{
    label: React.ReactNode;
    isSelected: boolean;
    onClick: () => void;
}> = ({ label, isSelected, onClick }) => {
    return (
        <button
            onClick={onClick}
            type="button"
            style={
                isSelected
                    ? ({
                          '--date-button-on-background-color':
                              'var(--date-button-active-color,var(--on-primary-color))',
                          '--date-button-background-color':
                              'var(--date-button-active-background-color,var(--primary-color))'
                      } as React.CSSProperties)
                    : {}
            }
            className={classnames(
                'text-[length:var(--date-button-font-size)] font-bold relative',
                'w-fit min-w-[90px] grow shrink-0 h-[64px] rounded-4 p-12 flex flex-col items-center justify-center space-x-10',
                'bg-[var(--date-button-background-color)]',
                'text-[var(--date-button-on-background-color)]'
            )}>
            {label}
        </button>
    );
};

/**
 * Pure display component for date filter buttons.
 * Receives showing counts externally — does NOT fetch data itself.
 * `showingsCounts` is an array of 8 values: [day0, day1, ..., day6, all]. Each is the number,
 * `undefined` while its request is in flight (rendered as a skeleton), or `null` when no count is
 * obtainable — no location, no cinema selected — in which case the badge is omitted instead of
 * showing a skeleton that would never resolve.
 */
export const DatesFilterDisplay: React.FC<{
    showButtons?: boolean;
    showingsCounts: (number | null | undefined)[];
    // First day of the seven day buttons; defaults to today. Callers passing a different
    // start day must derive `showingsCounts` from the same window.
    startDayKey?: DayKey;
    // When true and `previewsCount` resolves to > 0, a PREVIEWS tab is rendered
    // between ALL and the day buttons. The tab is hidden entirely when no previews exist.
    showPreviews?: boolean;
    previewsCount?: number;
    showAllTab?: boolean;
    // While the caller is still working out which day the buttons start at, the whole strip is a
    // skeleton. Rendering a today-based window first and moving it once the real start arrives
    // would relabel every button under the cursor.
    isStartDateLoading?: boolean;
}> = ({
    showButtons = false,
    showingsCounts,
    startDayKey,
    showPreviews = false,
    previewsCount,
    showAllTab = true,
    isStartDateLoading = false
}) => {
    const { selectedDate, setSelectedDate, setActiveTab, initialTab } = useRequiredShowtimesFinder();
    const t = useTranslations();
    const { track } = useAnalytics();
    // Pinned to the finder's zone rather than left to the ambient one: a host app may provide its
    // own TimeZoneProvider, and a button labeled in one zone while its day key names another is the
    // bug this strip is here to avoid.
    const {
        dateTime: { formatWeekday, formatDayMonth }
    } = useFormat({ timeZone: SHOWTIMES_TIME_ZONE });
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { styleFadedState } = useScrollState({
        scrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        },
        orientation: 'horizontal'
    });

    const showingsCountsForDays = useMemo(() => showingsCounts.slice(0, 7), [showingsCounts]);
    const countAll = showingsCounts[7];

    const dateButtonDayKeys = useMemo(
        () => Array.from({ length: 7 }, (_, index) => addCalendarDays(startDayKey ?? todayShowtimesDayKey(), index)),
        [startDayKey]
    );

    useEffect(() => {
        setActiveTab(getActiveDateTab(selectedDate, dateButtonDayKeys));
    }, [selectedDate, dateButtonDayKeys, setActiveTab]);

    const dates = useMemo(() => {
        const todayDayKey = todayShowtimesDayKey();
        return dateButtonDayKeys.map((dayKey, index) => {
            // Formatted from the day's 04:00 Berlin start, not from a browser-zone midnight: a
            // midnight `Date` read in Berlin names the day before for every viewer east of it.
            const dayStart = showtimesDayStart(dayKey);
            const dayLabel =
                dayKey === todayDayKey ? t('showtimes-finder.date.today') : formatWeekday(dayStart, 'short');
            const dateLabel = formatDayMonth(dayStart);
            return {
                label: (
                    <span
                        key={`DateButtonContent-${dayKey}`}
                        className="flex flex-col">
                        <span className="absolute top-0 right-4 leading-[18px]">
                            {showingsCountsForDays[index] === null ? null : showingsCountsForDays[index] ===
                              undefined ? (
                                <span className="inline-block animate-pulse-skeleton bg-[color-mix(in_srgb,var(--date-button-on-background-color)_50%,transparent)] rounded-2 w-10 h-10" />
                            ) : (
                                <span className="text-body-bold-10 opacity-50">{showingsCountsForDays[index]}</span>
                            )}
                        </span>
                        <span>{dayLabel}</span>

                        <span>{dateLabel}</span>
                    </span>
                ),
                date: dayKey
            };
        });
    }, [showingsCountsForDays, dateButtonDayKeys, formatWeekday, formatDayMonth, t]);

    if (isStartDateLoading) {
        return (
            <div className="relative">
                <div className="w-full p-12 flex flex-row space-x-6 overflow-x-hidden">
                    {(showAllTab ? DATE_BUTTON_SKELETON_KEYS : DATE_BUTTON_SKELETON_KEYS.slice(1)).map((key) => (
                        <div
                            key={`DateButtonSkeleton-${key}`}
                            className="w-fit min-w-[90px] grow shrink-0 h-[64px] rounded-4 animate-pulse-skeleton bg-[var(--date-button-background-color)]"
                        />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <div
                ref={scrollContainerRef}
                style={styleFaded(styleFadedState, 10)}
                className="w-full p-12 flex flex-row space-x-6 overflow-x-auto no-scrollbar">
                {showAllTab && (
                    <DateButton
                        label={
                            <span
                                key="DateButtonContent-all"
                                className="flex flex-col">
                                <span className="absolute top-0 right-4 leading-[18px]">
                                    {countAll === null ? null : countAll === undefined ? (
                                        <span className="inline-block animate-pulse-skeleton bg-[color-mix(in_srgb,var(--date-button-on-background-color)_50%,transparent)] rounded-2 w-10 h-10" />
                                    ) : (
                                        <span className="text-body-bold-10 opacity-50">{countAll}</span>
                                    )}
                                </span>
                                <span>{t('showtimes-finder.date.all')}</span>
                            </span>
                        }
                        isSelected={selectedDate === 'all'}
                        onClick={() => {
                            setSelectedDate('all');
                            track('Date Selected', {
                                day: 'all',
                                initialTab
                            });
                        }}
                    />
                )}
                {showPreviews && previewsCount !== undefined && previewsCount > 0 && (
                    <DateButton
                        label={
                            <span
                                key="DateButtonContent-previews"
                                className="flex flex-col">
                                <span className="absolute top-0 right-4 leading-[18px]">
                                    <span className="text-body-bold-10 opacity-50">{previewsCount}</span>
                                </span>
                                <span>{t('showtimes-finder.date.previews')}</span>
                            </span>
                        }
                        isSelected={selectedDate === PREVIEWS_SELECTED_DATE}
                        onClick={() => {
                            setSelectedDate(PREVIEWS_SELECTED_DATE);
                            track('Date Selected', {
                                day: PREVIEWS_SELECTED_DATE,
                                initialTab
                            });
                        }}
                    />
                )}
                {dates.map((date, index) => (
                    <DateButton
                        key={`DateButton-${date.date}`}
                        label={date.label}
                        isSelected={selectedDate === date.date}
                        onClick={() => {
                            setSelectedDate(date.date);
                            track('Date Selected', {
                                day: index,
                                date: date.date,
                                initialTab
                            });
                        }}
                    />
                ))}
            </div>
            {showButtons && (
                <BackAndForthButtonOverlay
                    scrollContainerRef={scrollContainerRef}
                    buttonUnavailableStyle="disabled"
                    renderOutside
                    renderOnMobile={true}
                />
            )}
        </div>
    );
};

/**
 * Self-contained date filter that fetches nearby showing counts internally.
 * Used by consumers that only need the "nearby" data source (e.g. ShowtimesFinder, ShowtimesFinderMenu).
 */
const DatesFilter: React.FC<{
    showButtons?: boolean;
    showPreviews?: boolean;
    showAllTab?: boolean;
}> = ({ showButtons = false, showPreviews = false, showAllTab = true }) => {
    const { movieId, apiFilters, releaseDate, selectedDate, setSelectedDate, setInitialTab } =
        useRequiredShowtimesFinder();

    // Previews only make sense while the release day still lies ahead; otherwise there is
    // no "now → day before the release date" window left to list.
    const isPreviewsEnabled = showPreviews && isBeforeReleaseDate(releaseDate);

    const { totalItems: previewsCount } = useCinemasShowingsSearch(movieId, PREVIEWS_SELECTED_DATE, apiFilters, {
        enabled: isPreviewsEnabled,
        releaseDate
    });

    // Only needed on the no-PREVIEWS path, where it — not the release date — decides where the
    // day buttons begin, so the query stays off whenever the tab is live.
    const { earliestShowingStartDatetime, isLoading: isEarliestShowingLoading } = useEarliestShowingDate(
        movieId,
        apiFilters,
        { enabled: !isPreviewsEnabled }
    );

    const startDayKey = useMemo(
        () => getDateButtonsStartDayKey({ releaseDate, isPreviewsEnabled, earliestShowingStartDatetime }),
        [releaseDate, isPreviewsEnabled, earliestShowingStartDatetime]
    );

    const nearestDates = useMemo(
        () => Array.from({ length: 7 }, (_, index) => addCalendarDays(startDayKey, index)),
        [startDayKey]
    );

    const isPreviewsAvailable = isPreviewsEnabled && previewsCount !== undefined && previewsCount > 0;

    // The counts only load once a location is known; without one the count is never coming and
    // must not be waited on.
    const { isAllLocationDetailsAvailable, isLocationInitialized } = useLocationStore();
    const isPreviewsCountSettled =
        isLocationInitialized && (!isAllLocationDetailsAvailable() || previewsCount !== undefined);

    // Which tabs the strip ends up with is still open; acting on a selection now moves the
    // visitor twice.
    const isStripResolving = isEarliestShowingLoading || (isPreviewsEnabled && !isPreviewsCountSettled);

    const isSelectedDateAvailable = isSelectedDateOnStrip({
        selectedDate,
        showAllTab,
        isPreviewsAvailable,
        startDayKey
    });

    // Never leave the finder stuck on a view no button leads back to.
    const fallbackSelectedDate = resolveFallbackSelectedDate({ showAllTab, isPreviewsAvailable, startDayKey });
    useEffect(() => {
        if (isStripResolving || isSelectedDateAvailable) {
            return;
        }
        setSelectedDate(fallbackSelectedDate);
    }, [isStripResolving, isSelectedDateAvailable, fallbackSelectedDate, setSelectedDate]);

    // Recorded once the fallback above has had its say: a configured PREVIEWS that turns out to be
    // empty is not the tab the visitor opened on.
    useEffect(() => {
        if (isStripResolving || !isSelectedDateAvailable) {
            return;
        }
        setInitialTab(getActiveDateTab(selectedDate, nearestDates));
    }, [isStripResolving, isSelectedDateAvailable, selectedDate, nearestDates, setInitialTab]);

    // Counting against a start date that is about to move would throw all eight results away and
    // refetch them a render later. The ALL count is only shown on the ALL tab, so a widget that
    // hides it does not pay for it.
    const countOptions = useMemo(() => ({ enabled: !isEarliestShowingLoading }), [isEarliestShowingLoading]);
    const allCountOptions = useMemo(
        () => ({ enabled: !isEarliestShowingLoading && showAllTab }),
        [isEarliestShowingLoading, showAllTab]
    );

    const { totalItems: showingsCountDay0 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[0],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountDay1 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[1],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountDay2 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[2],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountDay3 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[3],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountDay4 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[4],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountDay5 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[5],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountDay6 } = useCinemasShowingsSearch(
        movieId,
        nearestDates[6],
        apiFilters,
        countOptions
    );
    const { totalItems: showingsCountAll } = useCinemasShowingsSearch(movieId, 'all', apiFilters, allCountOptions);

    const showingsCounts = useMemo(
        () => [
            showingsCountDay0,
            showingsCountDay1,
            showingsCountDay2,
            showingsCountDay3,
            showingsCountDay4,
            showingsCountDay5,
            showingsCountDay6,
            showingsCountAll
        ],
        [
            showingsCountDay0,
            showingsCountDay1,
            showingsCountDay2,
            showingsCountDay3,
            showingsCountDay4,
            showingsCountDay5,
            showingsCountDay6,
            showingsCountAll
        ]
    );

    return (
        <DatesFilterDisplay
            showButtons={showButtons}
            showingsCounts={showingsCounts}
            startDayKey={startDayKey}
            showPreviews={isPreviewsEnabled}
            previewsCount={previewsCount}
            showAllTab={showAllTab}
            isStartDateLoading={isEarliestShowingLoading}
        />
    );
};

export default DatesFilter;
